import { useEffect, useRef, useState } from "react";
import { getContrastColor } from "@miplatform/utils";
import { Marker, useMap } from "@vis.gl/react-google-maps";

interface DirectionsRouteProps {
  disableDurationMarkers?: boolean;
  combineDurationMarkers?: boolean;
  points: {
    lat: number;
    lng: number;
  }[];
  textColor?: string;
  color?: string;
}

interface DurationInfo {
  id: string;
  text: string;
  position: google.maps.LatLngLiteral;
}

export const DirectionsRoute = ({
  disableDurationMarkers,
  combineDurationMarkers = false,
  points = [],
  color = "#000000",
  textColor = getContrastColor(color),
}: DirectionsRouteProps) => {
  const map = useMap();
  const rendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const serviceRef = useRef<google.maps.DirectionsService | null>(null);
  const [durationInfos, setDurationInfos] = useState<DurationInfo[]>([]);

  useEffect(() => {
    if (!map || points.length < 2) {
      setDurationInfos([]);
      return () => {
        rendererRef.current?.setMap(null);
        rendererRef.current = null;
      };
    }

    if (!rendererRef.current) {
      rendererRef.current = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: color,
          strokeWeight: 5,
        },
      });

      rendererRef.current.setMap(map);
    }

    if (!serviceRef.current) {
      serviceRef.current = new google.maps.DirectionsService();
    }

    const origin = points[0];
    const destination = points[points.length - 1];

    const waypoints = points.slice(1, -1).map((point) => ({
      location: point,
      stopover: !combineDurationMarkers,
    }));

    serviceRef.current.route(
      {
        origin,
        destination,
        waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && result) {
          rendererRef.current?.setDirections(result);
          if (!disableDurationMarkers && result.routes[0]?.legs) {
            const legInfos: DurationInfo[] = result.routes[0].legs.map(
              (leg, index) => {
                const path = leg.steps.flatMap((step) => step.path);
                const midIndex = Math.floor(path.length / 2);
                const midPoint = path[midIndex] || leg.start_location;

                return {
                  id: `leg-${index}`,
                  text: leg.duration?.text || "",
                  position: {
                    lat: midPoint.lat(),
                    lng: midPoint.lng(),
                  },
                };
              }
            );

            setDurationInfos(legInfos);
          } else {
            setDurationInfos([]);
          }
        }
      }
    );

    return () => {
      rendererRef.current?.setMap(null);
      rendererRef.current = null;
    };
  }, [points, color, disableDurationMarkers, combineDurationMarkers, map]);

  if (!durationInfos.length) return null;

  return (
    <>
      {durationInfos.map((durationInfo) => {
        const markerIcon = getDurationMarker(
          durationInfo.text,
          color,
          textColor
        );
        return (
          <Marker
            key={durationInfo.id}
            position={durationInfo.position}
            icon={markerIcon}
          />
        );
      })}
    </>
  );
};

const getDurationMarker = (
  text: string,
  color: string,
  textColor: string
): google.maps.Icon => {
  const charWidth = 8.5;
  const paddingX = 20;
  const minWidth = 60;

  const contentWidth = text.length * charWidth + paddingX;
  const rectWidth = Math.max(minWidth, contentWidth);
  const rectHeight = 28;

  const offsetRight = 18;
  const offsetTop = 18;

  const arrowWidth = 10;
  const arrowHeight = 16;

  const svgWidth = rectWidth + offsetRight + 12;
  const svgHeight = rectHeight + offsetTop + arrowHeight + 12;

  const rectX = 4;
  const rectY = 4;
  const borderRadius = 6;
  const rectBottomY = rectY + rectHeight;

  const arrowTipX = rectX;
  const arrowTipY = rectBottomY + arrowHeight;

  const pathData = `
    M ${rectX + borderRadius} ${rectY}
    H ${rectX + rectWidth - borderRadius}
    A ${borderRadius} ${borderRadius} 0 0 1 ${rectX + rectWidth} ${
    rectY + borderRadius
  }
    V ${rectBottomY - borderRadius}
    A ${borderRadius} ${borderRadius} 0 0 1 ${
    rectX + rectWidth - borderRadius
  } ${rectBottomY}
    H ${rectX + arrowWidth + borderRadius}
    L ${arrowTipX} ${arrowTipY}
    L ${rectX + borderRadius} ${rectBottomY}
    A ${borderRadius} ${borderRadius} 0 0 1 ${rectX} ${
    rectBottomY - borderRadius
  }
    V ${rectY + borderRadius}
    A ${borderRadius} ${borderRadius} 0 0 1 ${rectX + borderRadius} ${rectY}
    Z
  `;

  const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
    <defs>
      <filter id="shadow" x="-20%" y="-20%" width="150%" height="150%">
        <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.35"/>
      </filter>
    </defs>
    <g filter="url(#shadow)">
      <path fill="${color}" d="${pathData.replace(/\s+/g, " ").trim()}" />
      <text 
        x="${rectX + rectWidth / 2}" 
        y="${rectY + rectHeight / 2 + 4}" 
        fill="${textColor}" 
        font-family="system-ui, -apple-system, sans-serif" 
        font-size="13" 
        font-weight="600" 
        text-anchor="middle"
      >${text}</text>
    </g>
  </svg>`;

  return {
    url: `data:image/svg+xml,${encodeURIComponent(svgString)}`,
    anchor:
      typeof google !== "undefined"
        ? new google.maps.Point(arrowTipX, arrowTipY)
        : undefined,
  };
};
